// =============================================================================
// ms_2d_ui.jsx — StructureFlow 2D interface (React UI layer)
// -----------------------------------------------------------------------------
// Extracted from order_form_ui.jsx (2026-07-09) as the UI-side companion to the
// ms_2d_engine.js engine extraction. This is the ONE dedicated home for the 2D
// interface so 2D UI work is easy to find, own, and extend.
//
// What lives here: the whole 2D-only interface — the 3D/2D + Select/Measure/Edit
// toolbar, the Dim/Labels/Blueprint PDF controls, and the "Selected Opening" inspect
// panel (with the "i" help + Sketch/Edit numeric position controls). Both are
// portaled into #display3d, exactly as before.
//
// Boundary / contract (unchanged by the extraction):
//   - MS2DInterface is SELF-CONTAINED: it owns its own 2D state and talks only to
//     the engine via window.cp.* and to the shared globals React / ReactDOM.
//     It takes NO props, so there is no prop drilling from the main app.
//   - The engine (order_form_init.js / ms_2d_engine.js) pushes the selected
//     opening into this component via window.cp.onTwoDSelectionChange(info).
//   - order_form_ui.jsx stays the UI shell/orchestrator and simply renders
//     <MS2DInterface /> inside StructureDesignerApp.
//
// Build / load: compiled to ms_2d_ui.js (Babel) and loaded as a classic script
// BEFORE order_form_ui.js in cp_structures.libraries.yml, so the MS2DInterface
// global exists when the main app renders. No module system is used (same
// shared-global pattern as the rest of the configurator JS).
// =============================================================================

// ---- FEATURE SWITCHES -------------------------------------------------------
// One-line on/off flags. Flip a flag to false, rebuild ms_2d_ui.js, bump the
// library version, and deploy — no JSX block-commenting needed.
//
// (The old INTERIOR_VIEW_ENABLED flag is gone — the Interior button is always on now,
//   gated per-model by the engine: it only renders when the engine reports the active
//   model supports visual interior options, via cp.onInteriorViewChange.)
//
// BLUEPRINT_2D_ENABLED: the whole 2D / blueprint interface — the "2D" entry button,
//   the Select/Measure/Edit + Dim/Labels/Blueprint PDF tool cluster, and the "Selected Opening"
//   inspect panel. false = none of the 2D UI renders (the app stays in 3D; the 2D
//   engine in order_form_init.js / ms_2d_engine.js just goes unused/harmless).
const BLUEPRINT_2D_ENABLED = true;
// -----------------------------------------------------------------------------

// 2D interface: 3D/2D toggle + Select/Measure/Edit tools + Dim/Labels/export
// controls + the "Selected Opening" inspect panel. All 2D-only UI lives here so
// it stays out of the main UI shell (StructureDesignerApp in order_form_ui.jsx).
class MS2DInterface extends React.Component {
  constructor(props) {
    super(props);
    this.setViewMode = this.setViewMode.bind(this);
    this.toggleTwoDLayer = this.toggleTwoDLayer.bind(this);
    this.setTwoDTool = this.setTwoDTool.bind(this);
    this.nudgePosition = this.nudgePosition.bind(this);
    this.applyTypedPosition = this.applyTypedPosition.bind(this);
    this.toggleInspectHelp = this.toggleInspectHelp.bind(this);
    this.toggleInterior = this.toggleInterior.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleDocumentPointer = this.handleDocumentPointer.bind(this);
    this.toggleExportMenu = this.toggleExportMenu.bind(this);
    this.runExport = this.runExport.bind(this);
    this.openBlueprintOptions = this.openBlueprintOptions.bind(this);
    this.closeBlueprintOptions = this.closeBlueprintOptions.bind(this);
    this.generateBlueprintPdf = this.generateBlueprintPdf.bind(this);
    this.toggleViewNav = this.toggleViewNav.bind(this);
    this.openViewNav = this.openViewNav.bind(this);
    this.scheduleCloseViewNav = this.scheduleCloseViewNav.bind(this);
    this.syncViewState = this.syncViewState.bind(this);
    this.exportMenuNode = null;
    // Hover-intent timer for the radial HUD: the satellites render outside the wrap's
    // 44px box, so crossing the puck->satellite gap fires onMouseLeave. Delaying the
    // close by 250ms lets the pointer reach a satellite before the fan collapses.
    this.viewNavCloseTimer = null;

    // isNarrow drives the mobile layout (inspect panel moves to the bottom so it never
    // covers the top-left toolbar at ~390px). MS2D_MOBILE_MAX is the breakpoint.
    // Blueprint PDF popup starts on the shared export defaults (cp.blueprintDefaultOptions in
    // ms_2d_engine.js, also used by the order-save blueprint). Building dimensions has no popup
    // control: the engine reads the company's blueprint_show_dimensions itself.
    const bp = cp.blueprintDefaultOptions();
    this.state = {
      viewMode: '3d', dimOn: true, labelsOn: true, twoDSelection: null, twoDTool: 'measure',
      editError: false, inspectHelpOpen: false, interiorOn: false, interiorSupported: false, exportMenuOpen: false,
      blueprintOptionsOpen: false, blueprintPaper: bp.paperSize, blueprintOrientation: bp.orientation,
      blueprintScale: bp.scaleMode, blueprintProject: '', blueprintTitle: bp.sheetTitle,
      blueprintShowLabels: bp.showLabels, blueprintShowMeasurements: bp.showMeasurements,
      blueprintMessage: '', isNarrow: MS2DInterface.isNarrowNow(),
      // Radial view-nav HUD (zoom in / out / reset). viewZoom mirrors cp.two_d_zoom so the
      // readout and the disabled states follow wheel + pinch input, not just HUD clicks.
      viewNavOpen: false, viewNavPinned: false, viewNavHover: null,
      viewZoom: 1, viewZoomMin: 1, viewZoomMax: 8,
    };
  }

  static isNarrowNow() {
    return (typeof window !== 'undefined') && window.innerWidth <= 480;
  }

  // Register the 2D selection bridge so the engine (order_form_init.js) can push the
  // currently selected opening into React state to drive the inspect panel.
  componentDidMount() {
    if(window.cp) {
      window.cp.onTwoDSelectionChange = (info) => this.setState({twoDSelection: info || null, editError: false});
      // Interior view bridge: ms_interior_ui.js owns the cp.onInteriorViewChange binding
      // and pushes interiorOn/interiorSupported into this component's state.
      window.SFInteriorUI.bind(this);
    }
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('pointerdown', this.handleDocumentPointer);
    // The engine keeps no observer list, so order_form_init.js fires this after wheel,
    // pinch or drag-pan navigation and we re-read the authoritative view state.
    window.addEventListener('cp:two-d-view', this.syncViewState);
  }

  componentWillUnmount() {
    if(window.cp && window.cp.onTwoDSelectionChange) {
      window.cp.onTwoDSelectionChange = null;
    }
    window.SFInteriorUI.unbind(this);
    if(this.viewNavCloseTimer) {
      clearTimeout(this.viewNavCloseTimer);
      this.viewNavCloseTimer = null;
    }
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('pointerdown', this.handleDocumentPointer);
    window.removeEventListener('cp:two-d-view', this.syncViewState);
  }

  // Pull zoom + limits from the engine. Never derive them locally: the engine clamps, so
  // its number is the only one that matches what is actually on screen.
  syncViewState() {
    const get = window.cp && window.cp.getTwoDViewState;
    if(typeof get !== 'function') return;
    const v = get();
    if(!v) return;
    if(v.zoom !== this.state.viewZoom || v.zoom_max !== this.state.viewZoomMax) {
      this.setState({viewZoom: v.zoom, viewZoomMin: v.zoom_min, viewZoomMax: v.zoom_max});
    }
  }

  // Radial HUD actions. Each one calls the engine, then re-reads the clamped result rather
  // than assuming the requested value was applied.
  runViewNav(action) {
    const cp = window.cp;
    if(!cp) return;
    if(action === 'in' && typeof cp.zoomTwoDBy === 'function') { cp.zoomTwoDBy(1.25); }
    else if(action === 'out' && typeof cp.zoomTwoDBy === 'function') { cp.zoomTwoDBy(0.8); }
    else if(action === 'reset' && typeof cp.resetTwoDView === 'function') { cp.resetTwoDView(); }
    this.syncViewState();
  }

  // Click-to-pin: toggles the pinned state so the fan stays open while the user clicks
  // +, -, or Reset. Hover (desktop) still opens the fan for discoverability, but pinning
  // overrides hover-leave so the fan does not collapse when the pointer moves to a satellite.
  // When UNPINNING on desktop, also clear viewNavOpen: the pointer is still hovering the
  // wrap so viewNavOpen would keep the fan open otherwise. onMouseEnter won't re-fire until
  // the pointer leaves and re-enters, so the fan stays closed until then.
  toggleViewNav() {
    if(this.state.viewNavPinned) {
      this.setState({viewNavPinned: false, viewNavOpen: false, viewNavHover: null});
    } else {
      this.setState({viewNavPinned: true});
    }
  }

  // Hover-intent: entering the puck or any satellite cancels a pending close and opens
  // the fan immediately.
  openViewNav() {
    if(this.viewNavCloseTimer) {
      clearTimeout(this.viewNavCloseTimer);
      this.viewNavCloseTimer = null;
    }
    if(!this.state.viewNavOpen) { this.setState({viewNavOpen: true}); }
  }

  // Leaving the cluster starts a 250ms grace period instead of closing instantly, so the
  // pointer can cross the gap between the 44px puck and the fanned-out satellites.
  scheduleCloseViewNav() {
    if(this.viewNavCloseTimer) { clearTimeout(this.viewNavCloseTimer); }
    this.viewNavCloseTimer = setTimeout(() => {
      this.viewNavCloseTimer = null;
      this.setState({viewNavOpen: false, viewNavHover: null});
    }, 250);
  }

  // Keep isNarrow in sync so the mobile inspect-panel placement responds to
  // resize / device rotation without a reload.
  handleResize() {
    const narrow = MS2DInterface.isNarrowNow();
    if(narrow !== this.state.isNarrow) { this.setState({isNarrow: narrow}); }
  }

  // Compact export menu: keeps four export actions out of the already-tight toolbar.
  // Pointer-down outside closes it; actions close before invoking the engine download.
  handleDocumentPointer(e) {
    if((this.state.exportMenuOpen || this.state.blueprintOptionsOpen) &&
       this.exportMenuNode && !this.exportMenuNode.contains(e.target)) {
      this.setState({exportMenuOpen: false, blueprintOptionsOpen: false, blueprintMessage: ''});
    }
    // NOTE: the pinned radial HUD is deliberately NOT collapsed by outside clicks. A
    // pinned fan stays open while the user pans, drags openings, or taps the canvas, so
    // the zoom controls remain accessible during work. It only collapses on an explicit
    // puck click (toggleViewNav) or when leaving 2D mode (setViewMode).
  }

  toggleExportMenu() {
    this.setState({
      exportMenuOpen: !this.state.exportMenuOpen,
      blueprintOptionsOpen: false,
      blueprintMessage: '',
    });
  }

  runExport(method) {
    const fn = window.cp && window.cp[method];
    this.setState({exportMenuOpen: false}, () => {
      if(typeof fn === 'function') fn();
    });
  }

  openBlueprintOptions() {
    let project = this.state.blueprintProject;
    if(!project && window.cp && window.cp.order_number) {
      project = 'Order ' + window.cp.order_number;
    }
    // Display options always reopen on the shared defaults (the company's Blueprint Settings);
    // paper / orientation / scale / title keep whatever the user last chose.
    const bp = cp.blueprintDefaultOptions();
    this.setState({
      exportMenuOpen: false,
      blueprintOptionsOpen: true,
      blueprintProject: project,
      blueprintShowLabels: bp.showLabels,
      blueprintShowMeasurements: bp.showMeasurements,
      blueprintMessage: '',
    });
  }

  closeBlueprintOptions() {
    this.setState({blueprintOptionsOpen: false, blueprintMessage: ''});
  }

  generateBlueprintPdf() {
    const cp = window.cp;
    const fn = cp && cp.exportTwoDBlueprintPdf;
    if(typeof fn !== 'function') {
      this.setState({blueprintMessage: 'Blueprint export is not available yet.'});
      return;
    }
    // Measurements is fully self-contained: when checked we capture the detailed
    // start/width/end chain for every eligible opening just for this export, then
    // release it so the live Blueprint stays exactly as it was.
    const wantMeasurements = this.state.blueprintShowMeasurements;
    let captured = false;
    if(wantMeasurements && typeof cp.measureAllAndLock === 'function') {
      captured = !!((cp.measureAllAndLock() || {}).valid);
    }
    let result;
    try {
      result = fn({
        paperSize: this.state.blueprintPaper,
        orientation: this.state.blueprintOrientation,
        scaleMode: this.state.blueprintScale,
        projectName: this.state.blueprintProject,
        sheetTitle: this.state.blueprintTitle,
        generatedDate: new Date().toLocaleDateString(),
        showLabels: this.state.blueprintShowLabels,
        showMeasurements: wantMeasurements,
      });
    } finally {
      if(captured && typeof cp.clearTwoDLockedMeasurements === 'function') {
        cp.clearTwoDLockedMeasurements();
      }
    }
    if(result && result.ok) {
      this.setState({blueprintOptionsOpen: false, blueprintMessage: ''});
    } else {
      this.setState({blueprintMessage: (result && result.message) || 'Blueprint PDF could not be generated.'});
    }
  }

  // Interior View (3D only): fade the roof + near walls so the buyer can see inside.
  // Routed through the SFInteriorUI bridge into the engine, which owns all the
  // material/opacity logic and echoes the resulting state back through the bridge's
  // binding — so no local flip here; the highlight only ever shows what the engine did.
  toggleInterior() {
    window.SFInteriorUI.toggle(this);
  }

  // 3D/2D toggle. Sets React state and calls the engine stubs. Never reloads the page.
  setViewMode(mode) {
    if(mode === this.state.viewMode) {
      // Already in this mode. But if Interior View is on and the user clicks 3D, treat it as a
      // "reset to plain exterior 3D" — turn Interior off so the button isn't a dead click.
      if(mode === '3d' && this.state.interiorOn) {
        window.SFInteriorUI.clearIfLeaving3D(this);
      }
      return;
    }
    if(mode === '2d') {
      // Interior View is a 3D-only concept; clear it when leaving 3D so returning to 3D
      // starts from the normal exterior (the engine echoes the change back into
      // interiorOn). Measure is the sole 2D interaction mode.
      this.setState({
        viewMode: mode, twoDTool: 'measure', editError: false,
        exportMenuOpen: false, blueprintOptionsOpen: false, blueprintMessage: '',
      });
      if(this.state.interiorOn) {
        window.SFInteriorUI.clearIfLeaving3D(this);
      }
      window.cp && window.cp.enter2DMode && window.cp.enter2DMode();
      window.cp && window.cp.setTwoDTool && window.cp.setTwoDTool('measure');
      this.syncViewState();
    } else {
      this.setState({
        viewMode: mode, exportMenuOpen: false, blueprintOptionsOpen: false,
        blueprintMessage: '', viewNavOpen: false, viewNavPinned: false, viewNavHover: null,
      });
      window.cp && window.cp.exit2DMode && window.cp.exit2DMode();
    }
  }

  // 2D active tool (Select | Measure | Sketch/Edit). Select = direct manipulation;
  // Measure = read-only segmented dimensions; Sketch/Edit = numeric position editing for
  // the selected opening. Drives cp.two_d_tool in the engine.
  setTwoDTool(tool) {
    if(tool === this.state.twoDTool) return;
    this.setState({twoDTool: tool, editError: false});
    window.cp && window.cp.setTwoDTool && window.cp.setTwoDTool(tool);
  }

  // Sketch/Edit: nudge the selected opening +/- inches along its wall through the real
  // production movement/validation chain. Invalid moves are reverted by the engine.
  nudgePosition(delta) {
    const res = window.cp && window.cp.twoDNudgeSelected && window.cp.twoDNudgeSelected(delta);
    this.setState({editError: !(res && res.ok)});
  }

  // Sketch/Edit: apply a typed center position (inches from wall start). Runs on Enter or
  // blur; routes through the same validation/commit chain. Invalid values are reverted.
  applyTypedPosition(e) {
    if(e.type === 'keydown' && e.key !== 'Enter') return;
    const res = window.cp && window.cp.twoDSetSelectedPosition && window.cp.twoDSetSelectedPosition(e.target.value);
    this.setState({editError: !(res && res.ok)});
    if(e.type === 'keydown') { e.target.blur(); }
  }

  // 2D display-layer toggles (dimensions / opening labels). Only meaningful in 2D mode.
  toggleTwoDLayer(layer) {
    const key = (layer === 'dimensions') ? 'dimOn' : 'labelsOn';
    const next = !this.state[key];
    this.setState({[key]: next});
    window.cp && window.cp.setTwoDLayer && window.cp.setTwoDLayer(layer, next);
  }

  // Toggle bar rendered into the top-left of the 3D canvas (#display3d) via a portal,
  // since the React root (#react-ui) is a sibling of the canvas, not a parent.
  renderViewToggle() {
    const target = document.getElementById('display3d');
    if(!target) return null;
    const vm = this.state.viewMode;
    const baseBtn = {
      border: 'none', cursor: 'pointer', padding: '5px 12px', fontSize: '12px',
      fontWeight: 600, borderRadius: '4px', background: 'transparent', color: '#555', lineHeight: 1,
    };
    const activeBtn = Object.assign({}, baseBtn, {background: '#333', color: '#fff'});
    // secondary layer toggles (smaller, lighter) shown only in 2D.
    // inline-flex + gap so the check icon and label sit on one optical baseline.
    const layerBtn = {
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      border: '1px solid #d5dbe1', cursor: 'pointer', padding: '4px 10px', fontSize: '11px',
      fontWeight: 500, borderRadius: '6px', background: '#ffffff', color: '#4b5563', lineHeight: 1,
      letterSpacing: '0.01em', transition: 'all 0.15s ease',
    };
    const layerBtnOn = Object.assign({}, layerBtn, {
      border: '1px solid #075985', background: '#075985', color: '#ffffff',
      fontWeight: 600, boxShadow: '0 1px 3px rgba(7, 89, 133, 0.45)',
    });
    // Inline SVG check, NOT the ✔ glyph. U+2714 has emoji presentation, so it falls back to
    // Apple Color Emoji / Noto Color Emoji — color bitmap fonts that ignore CSS `color`, which
    // rendered it dark brown on the blue pill. SVG strokes with currentColor: true white, crisp
    // at any DPI, weight controlled by strokeWidth.
    //
    // viewBox is CROPPED to the path's inked bounds (1.470), not the usual 0 0 24 24. The path
    // spans x 4→20, y 6→17, so in a 24-unit box only 11/24 of the height was ink — the mark
    // rendered 5.04px tall next to a 7.92px text cap height and read undersized/timid.
    // Insetting the box to the bounds plus half the stroke (1.5) makes the box equal the ink:
    //   x 4-1.5=2.5, y 6-1.5=4.5, w 21.5-2.5=19, h 18.5-4.5=14
    // At 11×8 CSS px the default xMidYMid-meet scale is 8/14 = 0.571, so the check now inks a
    // full 8px tall — matching cap height — and the stroke lands at 3×0.571 = 1.71px, matching
    // the 600-weight text stems. Ink now fills the box, which removed ~1.8px of dead space each
    // side, so the flex gap goes 5px → 6px to hold the same optical spacing.
    const checkIcon = (
      <svg width="11" height="8" viewBox="2.5 4.5 19 14" fill="none" aria-hidden="true"
        style={{flexShrink: 0, display: 'block'}}>
        <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="3"
          strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
    // Measure is the sole 2D interaction mode (select + drag + edit + dimensions).
    const toolBtnOn = {
      border: '1px solid #333', cursor: 'pointer', padding: '4px 9px', fontSize: '11px',
      fontWeight: 600, borderRadius: '4px', background: '#333', color: '#fff', lineHeight: 1,
    };
    const divider = {width: '1px', alignSelf: 'stretch', background: '#ddd', margin: '2px 3px'};
    const exportItem = {
      width: '100%', border: 'none', borderRadius: 0, background: '#fff', color: '#3f4b52',
      cursor: 'pointer', display: 'block', fontSize: '11px', fontWeight: 600, lineHeight: 1.2,
      padding: '8px 10px', textAlign: 'left',
    };
    const blueprintFieldLabel = {
      display: 'block', margin: '0 0 3px', color: '#64747d', fontSize: '9px',
      fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
    };
    const blueprintControl = {
      width: '100%', height: '29px', boxSizing: 'border-box', border: '1px solid #cbd3d8',
      borderRadius: '4px', background: '#fff', color: '#263841', fontSize: '11px', padding: '4px 7px',
    };
    const blueprintCheck = {
      display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#465861',
      cursor: 'pointer', fontSize: '10px', whiteSpace: 'nowrap',
    };
    // ── Toolbar shell (1.467, Interior placement 1.468) ──────────────────────────
    // Two independent pill boxes on one transparent flex row, 16px apart:
    //   A = view controls — [3D | 2D] always, + Interior in 3D
    //   B = 2D layer/export cluster — mounts only in 2D
    // Interior sits in A because it changes what you're LOOKING AT, like 3D/2D, so A is
    // wider in 3D than in 2D. The split that matters is keeping the 2D layer/export
    // tools off the view controls entirely.
    //
    // The wrapper KEEPS className="view-mode-toggle": twoDEventFromUI() in
    // order_form_init.js calls closest('.view-mode-toggle, ...) to decide a pointer
    // event is UI rather than canvas. closest() matches ancestors, so every button in
    // both boxes stays guarded and a toolbar press can never be read as an
    // empty-canvas tap that clears the selection + Measure set.
    //
    // The wrapper is transparent/borderless so only the two boxes read as surfaces.
    // Its inline boxShadow:'none' is also what defeats the
    // `#display3d.view-mode-2d .view-mode-toggle` box-shadow in order_form.css —
    // that rule styled the old single bar and would otherwise cast a shadow around
    // the now-invisible wrapper.
    const toolbarRow = {
      position: 'absolute', top: '10px', left: '10px', zIndex: 20,
      display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
      maxWidth: 'calc(100% - 20px)',
      background: 'transparent', border: 'none', boxShadow: 'none', padding: 0,
    };
    // Group frame: thin near-black outline (1.469). The old #d5dbe1 disappeared against
    // the white 2D canvas, so the groups had no edge. #333 matches the active 3D/2D
    // button so frame and buttons read as one family, and the inner inactive buttons keep
    // their light borders — dark frame defines the group, light controls sit inside it.
    const pillBox = {
      display: 'inline-flex', alignItems: 'center', background: '#ffffff',
      border: '1px solid #333', borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.14)',
    };
    const modeBox = Object.assign({}, pillBox, {gap: '4px', padding: '3px 4px'});
    const toolsBox = Object.assign({}, pillBox, {gap: '6px', padding: '3px 6px'});
    const bar = (
      <div className="view-mode-toggle" style={toolbarRow}>
        {/* Container A — mode switcher, plus Interior which belongs with the view
            controls (Mathew's call, 1.468): it changes what you're LOOKING AT, same as
            3D/2D, so it rides in this box rather than a separate one. A therefore widens
            in 3D and narrows in 2D — intended. */}
        <div style={modeBox}>
          <button type="button" style={vm === '3d' ? activeBtn : baseBtn} onClick={() => this.setViewMode('3d')}>3D</button>
          {BLUEPRINT_2D_ENABLED && (
            <button type="button" style={vm === '2d' ? activeBtn : baseBtn} onClick={() => this.setViewMode('2d')} title="2D schematic view">2D</button>
          )}
          {vm === '3d' && this.state.interiorSupported && (
            <React.Fragment>
              <span style={divider}></span>
              <button type="button" style={this.state.interiorOn ? layerBtnOn : layerBtn}
                onClick={this.toggleInterior}
                title="Interior View: see inside — fades the roof & near walls">Interior</button>
            </React.Fragment>
          )}
        </div>
        {/* Container B — 2D layer + export tools. Mounts beside A at the 16px row gap. */}
        {BLUEPRINT_2D_ENABLED && vm === '2d' && (
          <div style={toolsBox}>
            {/* Measure button removed (v1.459) — measure is the sole/default 2D tool, always active
                via twoDTool:'measure' initial state + setViewMode reset. No toggle needed in the UI.
                Dim/Labels/Blueprint PDF remain. Sequence: [3D | 2D] [ Dimensions | Labels | Blueprint PDF ]. */}
            <button type="button" style={this.state.dimOn ? layerBtnOn : layerBtn}
              onClick={() => this.toggleTwoDLayer('dimensions')} title="Show/hide overall dimensions">{this.state.dimOn ? checkIcon : null}<span>Dimensions</span></button>
            <button type="button" style={this.state.labelsOn ? layerBtnOn : layerBtn}
              onClick={() => this.toggleTwoDLayer('labels')} title="Show/hide opening labels">{this.state.labelsOn ? checkIcon : null}<span>Labels</span></button>
            <span style={divider}></span>
            <div ref={(node) => { this.exportMenuNode = node; }} style={{position: 'relative', display: 'inline-flex'}}>
              <button type="button" style={this.state.blueprintOptionsOpen ? layerBtnOn : layerBtn}
                onClick={this.openBlueprintOptions} aria-haspopup="dialog" aria-expanded={this.state.blueprintOptionsOpen}
                title="Download the 2D Blueprint PDF (scale, schedule, dimensions)">Blueprint PDF</button>
              {this.state.blueprintOptionsOpen && (
                <div role="dialog" aria-label="Blueprint PDF options" style={Object.assign({
                  position: 'absolute', top: 'calc(100% + 5px)', zIndex: 30,
                  width: this.state.isNarrow ? '290px' : '300px', boxSizing: 'border-box',
                  background: '#fff', border: '1px solid #bfcbd2', borderRadius: '7px',
                  boxShadow: '0 7px 22px rgba(31,42,48,0.24)', padding: '11px',
                }, this.state.isNarrow ? {left: 0, right: 'auto'} : {right: 0})}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '9px'}}>
                    <strong style={{color: '#17324d', fontSize: '12px'}}>Blueprint PDF</strong>
                    <button type="button" onClick={this.closeBlueprintOptions} aria-label="Close Blueprint PDF options"
                      style={{border: 'none', background: 'transparent', color: '#7b8991', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: 0}}>×</button>
                  </div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px'}}>
                    <label>
                      <span style={blueprintFieldLabel}>Paper</span>
                      <select value={this.state.blueprintPaper}
                        onChange={(e) => this.setState({blueprintPaper: e.target.value, blueprintMessage: ''})}
                        style={blueprintControl}>
                        <option value="letter">Letter (8.5×11)</option>
                        <option value="tabloid">Tabloid (11×17)</option>
                      </select>
                    </label>
                    <label>
                      <span style={blueprintFieldLabel}>Orientation</span>
                      <select value={this.state.blueprintOrientation}
                        onChange={(e) => this.setState({blueprintOrientation: e.target.value, blueprintMessage: ''})}
                        style={blueprintControl}>
                        <option value="auto">Auto (fit building)</option>
                        <option value="landscape">Landscape</option>
                        <option value="portrait">Portrait</option>
                      </select>
                    </label>
                  </div>
                  <label style={{display: 'block', marginTop: '7px'}}>
                    <span style={blueprintFieldLabel}>Scale</span>
                    <select value={this.state.blueprintScale}
                      onChange={(e) => this.setState({blueprintScale: e.target.value, blueprintMessage: ''})}
                      style={blueprintControl}>
                      <option value="auto">Auto Fit</option>
                      <option value="quarter">1/4&quot; = 1&apos;</option>
                      <option value="eighth">1/8&quot; = 1&apos;</option>
                    </select>
                  </label>
                  <label style={{display: 'block', marginTop: '7px'}}>
                    <span style={blueprintFieldLabel}>Project / Customer</span>
                    <input type="text" maxLength="60" value={this.state.blueprintProject}
                      onChange={(e) => this.setState({blueprintProject: e.target.value})}
                      placeholder="Optional" style={blueprintControl} />
                  </label>
                  <label style={{display: 'block', marginTop: '7px'}}>
                    <span style={blueprintFieldLabel}>Sheet title</span>
                    <input type="text" maxLength="50" value={this.state.blueprintTitle}
                      onChange={(e) => this.setState({blueprintTitle: e.target.value})}
                      placeholder="Blueprint" style={blueprintControl} />
                  </label>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginTop: '9px'}}>
                    <label style={blueprintCheck}>
                      <input type="checkbox" checked={this.state.blueprintShowLabels}
                        onChange={(e) => this.setState({blueprintShowLabels: e.target.checked})} /> Labels
                    </label>
                    <label style={blueprintCheck} title="Include detailed start / width / end measurements for every opening">
                      <input type="checkbox" checked={this.state.blueprintShowMeasurements}
                        onChange={(e) => this.setState({blueprintShowMeasurements: e.target.checked})} /> Measurements
                    </label>
                  </div>
                  <p style={{margin: '8px 0 0', color: '#78878f', fontSize: '9px', lineHeight: 1.35}}>
                    Generated {new Date().toLocaleDateString()}. Fixed scales export only when the drawing fits the selected sheet.
                  </p>
                  {this.state.blueprintMessage && (
                    <p role="alert" style={{margin: '8px 0 0', padding: '6px 7px', background: '#fff4e5',
                      border: '1px solid #edc98e', borderRadius: '4px', color: '#79551d', fontSize: '10px', lineHeight: 1.35}}>
                      {this.state.blueprintMessage}
                    </p>
                  )}
                  <div style={{display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '9px'}}>
                    <button type="button" onClick={this.closeBlueprintOptions}
                      style={Object.assign({}, layerBtn, {padding: '6px 10px'})}>Cancel</button>
                    <button type="button" onClick={this.generateBlueprintPdf}
                      style={Object.assign({}, layerBtn, {padding: '6px 10px', background: '#17324d', borderColor: '#17324d', color: '#fff'})}>
                      Generate PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
    return ReactDOM.createPortal(bar, target);
  }

  toggleInspectHelp() {
    this.setState({inspectHelpOpen: !this.state.inspectHelpOpen});
  }

  // 2D inspect/details panel. Portaled into #display3d, shown only in 2D when an
  // opening is selected. Reads the shared selection info pushed by the engine.
  renderInspectPanel() {
    if(!BLUEPRINT_2D_ENABLED) return null;
    const target = document.getElementById('display3d');
    if(!target) return null;
    if(this.state.viewMode !== '2d') return null;
    const sel = this.state.twoDSelection;
    if(!sel) return null;
    const rowLabel = {color: '#8a97a0', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 1px'};
    // The card's headline. No "Opening" caption above it — the panel title already says what
    // this is, so the label was a wasted line in a 210px column.
    const openingName = {color: '#1f2a30', fontSize: '14px', fontWeight: 600, lineHeight: 1.3, margin: '0 0 9px'};
    const nudgeBtn = {flex: '1 1 0', border: '1px solid #d0d7de', cursor: 'pointer', padding: '5px 0', fontSize: '11px', fontWeight: 600, borderRadius: '5px', background: '#ffffff', color: '#2b6a86', lineHeight: 1};
    // Selected attributes + add-ons, published by the engine and already filtered to what is
    // genuinely live on this opening (-None- and conditionally-off groups never arrive here).
    const attrs = sel.attributes || [];
    const addons = sel.addons || [];
    const hasChosen = (attrs.length + addons.length) > 0;
    const badgeWrap = {display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '0 0 9px'};
    const badgeBase = {fontSize: '11px', fontWeight: 500, lineHeight: 1.3, padding: '3px 8px', borderRadius: '6px', whiteSpace: 'normal'};
    // Attributes read as properties of the opening; add-ons are extra parts bolted to it, so
    // they carry the warm tone the drawing already uses for add-on linework.
    const attrBadge = Object.assign({}, badgeBase, {background: '#f0f4f8', border: '1px solid #d4e0eb', color: '#2c4a5e'});
    const addonBadge = Object.assign({}, badgeBase, {background: '#fef8eb', border: '1px solid #fae1a6', color: '#7c5800'});
    // Width / position show the engine's formatted label only (2′ 7″ or 31″ depending on the
    // company's blueprint_measurement_format). Raw inches are just a fallback if no label came.
    const detail = (label, raw) => label || (raw == null ? '—' : (Math.round(raw * 10) / 10) + '″');
    const metaRow = {display: 'flex', justifyContent: 'space-between', gap: '10px', margin: '0 0 3px'};
    // Desktop: compact panel pinned top-right. Mobile (<=480px): pin it to the bottom edge,
    // full-width, so it never overlaps the top-left toolbar (the known 390px collision).
    const panelPos = this.state.isNarrow
      ? {left: '10px', right: '10px', bottom: '10px', width: 'auto', maxHeight: '48%', overflowY: 'auto'}
      : {top: '10px', right: '10px', width: '210px'};
    const showControls = (this.state.twoDTool === 'sketch' || this.state.twoDTool === 'measure');
    const panel = (
      <div className="two-d-inspect" style={Object.assign({
        position: 'absolute', zIndex: 21,
        background: 'rgba(255,255,255,0.97)', border: '1px solid #e1e7ec', borderRadius: '8px',
        padding: '10px 12px', fontFamily: 'inherit',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)',
      }, panelPos)}>
        {/* Hover cannot be expressed inline, and order_form.css is SCSS-managed with no working
            compiler on this machine — so the rule is scoped here, next to the button it styles.
            !important is required: the button carries its resting background as an inline style,
            which otherwise outranks any stylesheet rule. */}
        <style>{'.two-d-inspect .two-d-nudge:hover{background:#f6f8fa !important;}'}</style>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
          <span style={{fontSize: '11px', fontWeight: 700, color: '#2b6a86', textTransform: 'uppercase', letterSpacing: '0.04em'}}>Selected Opening</span>
          <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <button type="button" aria-label="Opening details: wall, width and position"
              title="Details — wall, width, position"
              onClick={this.toggleInspectHelp}
              style={{width: '16px', height: '16px', borderRadius: '50%', padding: 0, lineHeight: '14px', fontSize: '11px', fontWeight: 700, fontStyle: 'italic', cursor: 'pointer',
                border: '1px solid ' + (this.state.inspectHelpOpen ? '#2b6a86' : '#b8c2c9'),
                background: this.state.inspectHelpOpen ? '#2b6a86' : '#fff',
                color: this.state.inspectHelpOpen ? '#fff' : '#6a7a83'}}>i</button>
            <button type="button" title="Deselect" onClick={() => window.cp && window.cp.twoDClearSelection && window.cp.twoDClearSelection()}
              style={{border: 'none', background: 'transparent', cursor: 'pointer', color: '#8a97a0', fontSize: '15px', lineHeight: 1, padding: 0}}>×</button>
          </span>
        </div>
        {this.state.inspectHelpOpen && (
          <div style={{background: '#f5f8fa', border: '1px solid #e0e7ec', borderRadius: '5px', padding: '8px 10px', margin: '0 0 10px', fontSize: '11px', lineHeight: 1.45, color: '#4a5860'}}>
            <p style={{margin: '0 0 5px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#8a97a0'}}>This opening</p>
            <p style={metaRow}><span>Wall</span><strong style={{color: '#1f2a30'}}>{(() => { let wn = ('' + (sel.wall_name || '')).replace(/\D+/g, ''); return wn ? ('Wall ' + wn) : (sel.wall_name || '—'); })()}</strong></p>
            <p style={metaRow}><span>Width</span><strong style={{color: '#1f2a30'}}>{detail(sel.width_label, sel.width)}</strong></p>
            <p style={Object.assign({}, metaRow, {marginBottom: '8px'})}><span>Position</span><strong style={{color: '#1f2a30'}}>{detail(sel.position_label, sel.position)}</strong></p>
            <p style={{margin: '0 0 5px'}}><strong style={{color: '#2b6a86'}}>Position</strong> — center of the opening, measured in from the start of its wall.</p>
            <p style={{margin: '0 0 5px'}}><strong style={{color: '#2b6a86'}}>Width</strong> — the opening’s width.</p>
            <p style={{margin: 0}}>The <strong style={{color: '#1e88e5'}}>blue box</strong> marks the active opening.{/* company 'Disable Drag Measurements' turns the tap-to-measure chain off, so don't describe it */}{!window.cp.settings.company.disable_drag_measurements && (<span> <strong style={{color: '#0f9d8c'}}>Teal dimension lines</strong> belong to the measured opening(s).</span>)}</p>
          </div>
        )}
        <p style={openingName}>{sel.label || '—'}</p>
        {hasChosen ? (
          <React.Fragment>
            <p style={rowLabel}>Selected</p>
            <div style={Object.assign({}, badgeWrap, {marginBottom: showControls ? '8px' : 0})}>
              {attrs.map((a, i) => (
                <span key={'attr' + i} style={attrBadge}
                  title={a.group ? a.group + ': ' + a.label : a.label}>{a.label}</span>
              ))}
              {addons.map((a, i) => (
                <span key={'addon' + i} style={addonBadge}
                  title={a.group ? a.group + ': ' + a.label : a.label}>{a.label}</span>
              ))}
            </div>
          </React.Fragment>
        ) : (
          // Nothing chosen on this opening. A muted one-liner rather than an empty "Selected"
          // header, so the panel reads as finished instead of broken.
          <p style={{color: '#8a97a0', fontSize: '11px', fontStyle: 'italic', lineHeight: 1.3,
            margin: showControls ? '0 0 8px' : 0}}>Standard configuration</p>
        )}
        {showControls && (
          // One positioning section: the divider separates it from the badges above, and the
          // nudge row and typed centre sit together as a single block of controls.
          <div style={{borderTop: '1px solid #e1e7ec', paddingTop: '9px'}}>
            <p style={rowLabel}>Move along wall</p>
            <div style={{display: 'flex', gap: '4px', margin: '0 0 9px'}}>
              <button type="button" className="two-d-nudge" style={nudgeBtn} onClick={() => this.nudgePosition(-6)} title="Move 6 inches toward wall start">−6″</button>
              <button type="button" className="two-d-nudge" style={nudgeBtn} onClick={() => this.nudgePosition(-1)} title="Move 1 inch toward wall start">−1″</button>
              <button type="button" className="two-d-nudge" style={nudgeBtn} onClick={() => this.nudgePosition(1)} title="Move 1 inch toward wall end">+1″</button>
              <button type="button" className="two-d-nudge" style={nudgeBtn} onClick={() => this.nudgePosition(6)} title="Move 6 inches toward wall end">+6″</button>
            </div>
            {/* The unit used to be buried in the label as "in from wall start"; it now sits beside
                the field, where someone typing a number will actually look for it. */}
            <p style={rowLabel}>Set center (from wall start)</p>
            <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
              <input type="number" step="1"
                key={'ep_' + (sel.wall_name || '') + '_' + (sel.position != null ? Math.round(sel.position) : '')}
                defaultValue={sel.position != null ? Math.round(sel.position) : ''}
                onKeyDown={this.applyTypedPosition} onBlur={this.applyTypedPosition}
                title="Type a position in inches and press Enter to apply"
                style={{flex: '1 1 auto', minWidth: 0, padding: '5px 7px', border: '1px solid #d0d7de', borderRadius: '5px', fontSize: '13px', boxSizing: 'border-box'}} />
              <span style={{flex: '0 0 auto', fontSize: '11px', fontWeight: 600, color: '#8a97a0', lineHeight: 1}}>in</span>
            </div>
            {this.state.editError
              ? <p style={{color: '#c0392b', fontSize: '11px', margin: '6px 0 0'}}>Can’t place there — reverted.</p>
              : <p style={{color: '#8a97a0', fontSize: '10px', margin: '6px 0 0'}}>Snaps, wall limits &amp; collisions apply.</p>}
          </div>
        )}
      </div>
    );
    return ReactDOM.createPortal(panel, target);
  }

  // Radial view-nav HUD: a dark-glass puck that fans out Zoom In / Reset / Zoom Out.
  // Portaled into #display3d like the other 2D controls, and its `.two-d-viewnav` class is
  // whitelisted in twoDEventFromUI (order_form_init.js) so pressing it can never be read as
  // an empty-space canvas tap that clears the selection.
  //
  // Placement is measured, not guessed. The toolbar owns the top-left, the inspect panel
  // the top-right (desktop) or the full-width bottom sheet (mobile), and a credit strip the
  // bottom-left. The bottom-right corner is the only zone that's free on every device.
  //
  // On mobile the bottom sheet (z-index 21) covers the bottom half when an opening is
  // selected. The HUD uses z-index 30 so it floats above the sheet and stays clickable —
  // the user needs zoom/pan most when inspecting an opening. The puck is slightly compact
  // on mobile (36px vs 44px) to reduce visual weight on the smaller canvas.
  renderViewNav() {
    if(!BLUEPRINT_2D_ENABLED) return null;
    const target = document.getElementById('display3d');
    if(!target) return null;
    if(this.state.viewMode !== '2d') return null;

    const narrow = this.state.isNarrow;
    const open = this.state.viewNavOpen || this.state.viewNavPinned;
    const zoom = this.state.viewZoom;
    const MAIN = narrow ? 36 : 44;   // puck diameter
    const SUB = narrow ? 28 : 34;    // satellite diameter
    const R = narrow ? 46 : 58;      // fan radius
    const c = MAIN / 2;

    // Fan angles in degrees, 0 = right and rising counter-clockwise. The bottom-right anchor
    // fans up + left on every device — the satellites travel INTO the canvas, never toward
    // the clipped edges.
    const actions = [{k: 'in', a: 90}, {k: 'reset', a: 135}, {k: 'out', a: 180}];

    const labels = {
      in:    {glyph: '+', head: 'Zoom In', hint: 'Click or scroll up', aria: 'Zoom in'},
      out:   {glyph: '\u2212', head: 'Zoom Out', hint: 'Click or scroll down', aria: 'Zoom out'},
      reset: {glyph: '\u2299', head: 'Fit to View', hint: 'Reset to default framing', aria: 'Reset view to fit'},
      puck:  {head: 'Blueprint View', hint: 'Scroll / Pinch to zoom \u2022 Drag to pan'},
    };
    const disabled = {
      in: zoom >= this.state.viewZoomMax - 0.0001,
      out: zoom <= this.state.viewZoomMin + 0.0001,
      reset: zoom <= this.state.viewZoomMin + 0.0001,
    };

    const wrap = Object.assign({
      position: 'absolute', zIndex: 30, width: MAIN + 'px', height: MAIN + 'px',
      // The fan renders outside this box; no clipping because the box has no overflow rule
      // and it fans inward, away from #display3d's own overflow:hidden edges.
      // z-index 30 floats above the mobile bottom-sheet drawer (z-index 21) so the zoom
      // controls stay accessible when an opening is selected.
    }, narrow
      ? {right: '12px', bottom: '90px'}   // clears the fixed mobile menu bar (83px tall)
      : {right: '14px', bottom: '14px'});

    const puck = {
      position: 'absolute', left: 0, top: 0, width: MAIN + 'px', height: MAIN + 'px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: '1px solid rgba(255,255,255,0.18)', borderRadius: '50%', cursor: 'pointer',
      background: this.state.viewNavPinned ? 'rgba(53,70,79,0.94)' : 'rgba(31,42,48,0.82)',
      color: '#f2f6f8', padding: 0,
      boxShadow: open ? '0 3px 14px rgba(15,25,30,0.42)' : '0 2px 8px rgba(15,25,30,0.32)',
      fontSize: (zoom > 1.05 ? 12 : 11) + 'px', fontWeight: 700, lineHeight: 1,
      WebkitBackdropFilter: 'blur(3px)', backdropFilter: 'blur(3px)',
      transition: 'box-shadow 120ms ease, background 120ms ease',
    };

    const satellite = (act) => {
      const rad = act.a * Math.PI / 180;
      const dx = Math.cos(rad) * R;
      const dy = -Math.sin(rad) * R;   // screen Y grows downward
      const off = disabled[act.k];
      const hot = this.state.viewNavHover === act.k;
      return {
        position: 'absolute',
        left: (c + dx - SUB / 2) + 'px',
        top: (c + dy - SUB / 2) + 'px',
        width: SUB + 'px', height: SUB + 'px', padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid ' + (hot && !off ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.16)'),
        borderRadius: '50%',
        background: off ? 'rgba(31,42,48,0.34)' : (hot ? 'rgba(53,70,79,0.94)' : 'rgba(31,42,48,0.86)'),
        color: off ? 'rgba(242,246,248,0.35)' : '#f2f6f8',
        cursor: off ? 'default' : 'pointer',
        fontSize: (act.k === 'reset' ? 15 : 17) + 'px', fontWeight: 600, lineHeight: 1,
        boxShadow: '0 2px 8px rgba(15,25,30,0.3)',
        WebkitBackdropFilter: 'blur(3px)', backdropFilter: 'blur(3px)',
        transition: 'background 120ms ease, border-color 120ms ease',
      };
    };

    // Custom dark-glass tooltip. Native title attributes can't be styled and clip
    // unpredictably, so the HUD renders its own. It sits to the LEFT of the entire cluster
    // (clear of every satellite, since the leftmost is at c - R) and never clips the
    // canvas corner the HUD lives in. pointerEvents:none keeps it from stealing hover.
    const tipStyle = {
      position: 'absolute', right: (MAIN + R + 10) + 'px', top: '50%',
      transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 25,
      padding: '6px 10px', background: 'rgba(20,28,33,0.92)',
      border: '1px solid rgba(255,255,255,0.14)', borderRadius: '6px',
      boxShadow: '0 4px 14px rgba(10,18,22,0.4)', color: '#f2f6f8',
      fontSize: '11px', fontWeight: 600, lineHeight: 1.35, whiteSpace: 'nowrap',
      WebkitBackdropFilter: 'blur(4px)', backdropFilter: 'blur(4px)',
    };
    const tipHintStyle = {
      color: 'rgba(242,246,248,0.62)', fontSize: '10px', fontWeight: 500, marginTop: '1px',
    };
    const hoverKey = this.state.viewNavHover;
    let tip = null;
    if(hoverKey && labels[hoverKey]) {
      const lab = labels[hoverKey];
      const head = (hoverKey === 'puck')
        ? lab.head + ' \u2014 ' + (Math.round(zoom * 10) / 10) + '\u00d7'
        : lab.head;
      tip = (
        <div style={tipStyle}>
          <div>{head}</div>
          {lab.hint && <div style={tipHintStyle}>{lab.hint}</div>}
        </div>
      );
    }

    const hud = (
      <div className="two-d-viewnav" style={wrap}
           onMouseEnter={narrow ? undefined : this.openViewNav}
           onMouseLeave={narrow ? undefined : this.scheduleCloseViewNav}>
        {/* ^ hover-leave only clears viewNavOpen, NOT viewNavPinned, so a pinned fan stays
             open when the pointer moves between the puck and the satellites. */}
        {tip}
        {open && actions.map((act) => (
          <button key={act.k} type="button" style={satellite(act)}
                  disabled={disabled[act.k]}
                  aria-label={labels[act.k].aria}
                  onMouseEnter={() => { this.openViewNav(); this.setState({viewNavHover: act.k}); }}
                  onMouseLeave={() => this.setState({viewNavHover: null})}
                  onClick={() => this.runViewNav(act.k)}>{labels[act.k].glyph}</button>
        ))}
        <button type="button" style={puck} onClick={this.toggleViewNav}
                aria-expanded={open} aria-haspopup="true"
                aria-label="View controls: zoom and reset"
                onMouseEnter={() => this.setState({viewNavHover: 'puck'})}
                onMouseLeave={() => this.setState({viewNavHover: null})}>
          {/* Zoomed in: the puck becomes the readout, which is more useful than an icon. */}
          {zoom > 1.05
            ? (Math.round(zoom * 10) / 10) + '\u00d7'
            : (
              <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden="true"
                   fill="none" stroke="currentColor" strokeWidth="1.7"
                   strokeLinecap="round">
                <circle cx="12" cy="12" r="7" />
                <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3" />
                <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
              </svg>
            )}
        </button>
      </div>
    );
    return ReactDOM.createPortal(hud, target);
  }

  render() {
    return (
      <React.Fragment>
        {this.renderViewToggle()}
        {this.renderInspectPanel()}
        {this.renderViewNav()}
      </React.Fragment>
    );
  }
}
